// Map orchestrator. Single grid: persistent left sidebar + map canvas.
// Map area also hosts the floating timeline (centered) and the slide-in
// event-detail panel (right). URL-stateful filter/selection store via
// useUrlState.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { EventMap, type CountryStatusRecord } from '../components/event-map';
import { TimelineScrubber } from '../components/timeline';
import { EventDetail } from '../components/event-detail';
import { Sidebar } from '../components/sidebar';
import { ViewToggle } from '../components/view-toggle';
import { LegalGate } from '../components/legal-gate';
import { OnboardingTour } from '../components/onboarding-tour';
import { CountryStatusSwitcher, type StatusDimension } from '../components/country-status-switcher';
import { fetchEvents, applyFilters } from '../lib/events';
import { useUrlState, readInitialCamera, isEmbedMode } from '../lib/url-state';
import { SeoHead } from '../components/seo-head';
import { mapPageJsonLd } from '../lib/seo';
import { config } from '../lib/config';
import type { EventRecord } from '../lib/events';

export function MapView() {
  const [events, setEvents] = useState<EventRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [{ filters, selectedId, viewMode }, update] = useUrlState();

  // Cage-bend #2: multi-dimensional country-status layer switcher.
  // statusDim controls which dimension the choropleth visualises.
  // countryStatus is keyed by country_iso2 for fast lookup in EventMap.
  const [statusDim, setStatusDim] = useState<StatusDimension>('burden');
  const [countryStatus, setCountryStatus] = useState<Record<string, CountryStatusRecord>>({});

  useEffect(() => {
    let cancelled = false;
    fetch('/api/country-status', { credentials: 'same-origin' })
      .then((r) => r.json())
      .then((records: CountryStatusRecord[]) => {
        if (cancelled) return;
        const byIso2: Record<string, CountryStatusRecord> = {};
        for (const rec of records) {
          if (rec.country_iso2) byIso2[rec.country_iso2] = rec;
        }
        setCountryStatus(byIso2);
      })
      .catch(() => { /* non-fatal: choropleth falls back to events-derived fill */ });
    return () => { cancelled = true; };
  }, []);
  // Camera params (lat/lng/zoom) are read once at mount — landing-page CTAs
  // bring users in with `?lat=…&lng=…&zoom=…` to focus a region. Filters
  // continue to flow through useUrlState; camera is one-shot and shouldn't
  // re-fire on every filter change.
  const initialCameraRef = useMemo(() => readInitialCamera(), []);
  // Embed mode (?embed=1) means the SPA is mounted inside an iframe on
  // a landing page. Skip the legal gate and onboarding tour — the parent
  // page owns those — and dim down the brand watermark.
  const embed = useMemo(() => isEmbedMode(), []);

  // First-visit policy: if the user lands on / with no URL filters and has
  // never been here before, pre-apply Andes + active so the live MV Hondius
  // and Argentina ANDV cluster are immediately visible. Default releases
  // the moment they touch any filter (handled by URL writes flipping a
  // localStorage marker the next time we check).
  useEffect(() => {
    try {
      const seen = window.localStorage.getItem(`${config.app.domain}:first-visit:v1`);
      const sp = new URLSearchParams(window.location.search);
      const urlHasFilters = ['p', 's', 'st', 'c', 'd0', 'd1', 'm0', 'm1', 'y0', 'y1', 'id', 'lat', 'lng']
        .some(k => sp.has(k));
      if (!seen && !urlHasFilters) {
        update(prev => ({
          ...prev,
          filters: {
            ...prev.filters,
            categories: new Set(['andes']),
            statuses: new Set(['active']),
          },
        }));
      }
      // Mark visited regardless — once the user has been here once, no more
      // forced defaults.
      window.localStorage.setItem(`${config.app.domain}:first-visit:v1`, new Date().toISOString());
    } catch { /* localStorage unavailable — silently skip */ }
    // Intentionally only run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await fetchEvents();
        if (!cancelled) {
          setEvents(rows);
          setLoading(false);
        }
      } catch (e: unknown) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'failed to load events');
          setLoading(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const visible = useMemo(() => applyFilters(events, filters), [events, filters]);
  const selected = useMemo(
    () => (selectedId ? events.find(o => o.id === selectedId) ?? null : null),
    [events, selectedId],
  );

  const handleSelect = useCallback(
    (id: string | null) => update(prev => ({ ...prev, selectedId: id })),
    [update],
  );

  useEffect(() => {
    if (!selectedId) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') handleSelect(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedId, handleSelect]);

  const tokenMissing = !import.meta.env.VITE_MAPBOX_TOKEN;

  if (tokenMissing) {
    return <SetupCard tokenMissing={tokenMissing} />;
  }

  return (
    <div
      className="relative h-screen w-screen overflow-hidden"
      style={{ background: '#070612' }}
    >
      <SeoHead
        title={config.app.name}
        description={config.app.description}
        canonical="/"
        jsonLd={[mapPageJsonLd()]}
      />
      {/* First-visit blocking acknowledgement. Self-dismisses after the user
          accepts and persists ack to localStorage so it never shows again.
          Suppressed in embed mode — parent landing page owns disclaimers. */}
      {!embed && <LegalGate />}
      {/* Optional, replayable feature tour. Auto-starts only after legal
          gate is accepted; otherwise the two would compete. Suppressed in
          embed mode where vertical space is precious and the parent page
          provides its own context. */}
      {!embed && <OnboardingTour />}
      <EventMap
        events={visible}
        selectedId={selectedId}
        onSelect={handleSelect}
        viewMode={viewMode}
        initialCamera={initialCameraRef ?? undefined}
        statusDim={statusDim}
        countryStatus={countryStatus}
      />

      {/* radial vignette for cosmic depth */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-10"
        style={{
          background:
            'radial-gradient(ellipse at center, transparent 35%, rgba(0,0,0,0.42) 100%)',
        }}
      />

      {/* Sidebar / view-toggle / timeline are all hidden in embed mode.
          A landing-page iframe is small and the map is the whole point;
          competing chrome covers the markers and obscures the map. The
          full SPA at / has all of these intact. */}
      {!embed && (
        <>
          {/* floating sidebar — top-left, inset, scrolls internally if tall */}
          <div className="pointer-events-none absolute left-4 top-4 z-20">
            <Sidebar
              events={events}
              visible={visible}
              filters={filters}
              onFilters={(next) => update(prev => ({ ...prev, filters: next }))}
              selectedId={selectedId}
              onSelect={handleSelect}
            />
          </div>

          {/* view-mode pill — top-right of the map area, hides when a detail
              panel is open so it doesn't compete for attention. Hidden on
              mobile to avoid stacking behind the sidebar header.
              In choropleth mode the country-status switcher (cage-bend #2)
              appears below the view toggle. */}
          {!selected && (
            <div data-tour="view-toggle" className="pointer-events-none absolute top-4 right-4 z-20 hidden sm:flex sm:flex-col sm:items-end sm:gap-2">
              <ViewToggle
                value={viewMode}
                onChange={(next) => update(prev => ({ ...prev, viewMode: next }))}
              />
              {viewMode === 'choropleth' && (
                <CountryStatusSwitcher
                  value={statusDim}
                  onChange={setStatusDim}
                />
              )}
            </div>
          )}

          {/* floating timeline — centered at bottom. Compact px on mobile so
              preset chips don't clip; max-w-2xl on desktop. */}
          <div className="pointer-events-none absolute inset-x-0 bottom-3 z-20 flex justify-center px-3 sm:bottom-4 sm:px-4">
            <div data-tour="timeline" className="surface-glass pointer-events-auto w-full max-w-2xl">
              <TimelineScrubber
                value={filters.dayRange}
                onChange={(dayRange) =>
                  update(prev => ({ ...prev, filters: { ...prev.filters, dayRange } }))
                }
                events={events}
              />
            </div>
          </div>
        </>
      )}

      {/* event detail — full screen on mobile, floating card on desktop */}
      {selected && (
        <div className="pointer-events-none fixed inset-2 z-30 sm:absolute sm:inset-auto sm:right-4 sm:top-4">
          <EventDetail event={selected} onClose={() => handleSelect(null)} />
        </div>
      )}

      {/* "Maps by Micky" credit, bottom-right above the Mapbox attribution.
          Hidden on mobile where vertical space is precious. In embed mode
          the credit becomes "Open full map ↗" so users in the
          iframe have a one-click escape hatch back to the full site. */}
      <a
        href={embed ? '/' : 'https://mickydollimore.com'}
        target={embed ? '_top' : '_blank'}
        rel="noopener noreferrer"
        className="surface-glass pointer-events-auto absolute bottom-4 right-4 z-20 hidden h-7 items-center gap-1 px-2.5 text-[11.5px] font-medium text-[var(--color-text-2)] transition-colors hover:text-[var(--color-accent)] sm:inline-flex"
      >
        {embed ? 'Open full map' : 'Maps by Micky'}
        <span aria-hidden className="text-[var(--color-text-3)]">↗</span>
      </a>

      {loading && <CornerStatus label="Loading events…" />}
      {error && <CornerStatus label={`Error: ${error}`} variant="danger" />}
      {!loading && !error && events.length === 0 && (
        <CornerStatus label="No events yet — run npm run seed" />
      )}

      <noscript>
        <div className="absolute inset-0 flex items-center justify-center bg-[var(--color-bg)] text-[var(--color-text-2)]">
          this site requires JavaScript
        </div>
      </noscript>
    </div>
  );
}

function CornerStatus({ label, variant }: { label: string; variant?: 'danger' }) {
  return (
    <div
      className={
        'fade-in surface-glass absolute right-4 top-4 z-30 px-3 py-2 text-[12px] font-medium ' +
        (variant === 'danger' ? 'text-[var(--color-signal)]' : 'text-[var(--color-text-2)]')
      }
    >
      {label}
    </div>
  );
}

function SetupCard({ tokenMissing }: { tokenMissing: boolean }) {
  return (
    <div className="flex h-screen items-center justify-center bg-[var(--color-bg)] px-6">
      <div className="surface w-full max-w-md p-6">
        <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-[var(--color-text-3)]">
          Setup required
        </div>
        <h1 className="mb-3 text-[20px] font-semibold leading-snug text-[var(--color-text)]">
          Drop your env vars in
        </h1>
        <p className="mb-4 text-[13.5px] leading-relaxed text-[var(--color-text-2)]">
          Create <code className="mono rounded bg-[var(--color-surface-2)] px-1.5 py-0.5 text-[12px] text-[var(--color-text)]">frontend/.env.local</code> with:
        </p>
        <pre className="mono mb-4 overflow-x-auto rounded-lg border border-[var(--color-line)] bg-[var(--color-surface-2)] p-3 text-[12px] leading-relaxed text-[var(--color-text)]">
{tokenMissing && 'VITE_MAPBOX_TOKEN=pk.your-public-mapbox-token\n'}
        </pre>
        <p className="text-[12px] text-[var(--color-text-3)]">
          See <code className="mono text-[var(--color-text)]">README.md</code> for full setup
        </p>
      </div>
    </div>
  );
}
